import React from 'react';
import HistoryTimeline from '@/shared/components/HistoryTimeline';
import {useEmailTemplateHistoryQuery} from '@/shared/hooks/useEmailTemplateHistoryQuery';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';

interface EmailTemplateHistoryPanelProps {
  projectId?: string | null;
}

const ALL_TEMPLATES = '__all__';

/**
 * Email Setup "History" tab (OD-1146). Reuses the shared timeline as-is for
 * the day-grouped list + All/By Users/By System filter (AC2, AC3, AC6, AC7);
 * layers a template filter on top (AC8) by narrowing the entries array
 * client-side before handing it to HistoryTimeline.
 */
export default function EmailTemplateHistoryPanel({projectId}: EmailTemplateHistoryPanelProps) {
  const {data: entries = [], isLoading, isError} = useEmailTemplateHistoryQuery({projectId});
  const [templateFilter, setTemplateFilter] = React.useState(ALL_TEMPLATES);

  const templates = React.useMemo(() => {
    const labels = new Set<string>();
    entries.forEach((e) => {
      const label = e.metadata?.template_label;
      if (label) labels.add(label);
    });
    return Array.from(labels).sort((a, b) => a.localeCompare(b));
  }, [entries]);

  const filtered = React.useMemo(() => {
    if (templateFilter === ALL_TEMPLATES) return entries;
    return entries.filter((e) => e.metadata?.template_label === templateFilter);
  }, [entries, templateFilter]);

  return (
    <div className="space-y-4">
      {templates.length > 0 && (
        <Select value={templateFilter} onValueChange={setTemplateFilter}>
          <SelectTrigger className="h-9 w-[260px]">
            <SelectValue placeholder="Filter by template" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_TEMPLATES}>All templates</SelectItem>
            {templates.map((label) => (
              <SelectItem key={label} value={label}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <HistoryTimeline
        entries={filtered}
        isLoading={isLoading}
        isError={isError}
        showFilter
        emptyLabel="No template history yet."
      />
    </div>
  );
}
